import { XMLSerializer } from '@xmldom/xmldom';
import uuid from 'uuid';
import interactionParserUtils from './InteractionParserUtils';
import { parseImage } from '../MediaModels';
import utilService from '../../../../services/UtilService';
import constants from '../../../Constants';

const serializer = new XMLSerializer();

const graphicGapMatchParser = {
    /**
     * @param {Element} rootElement Will be modified!
     * @param {string} language
     * @param {string} defaultLanguage
     * @returns {Array}
     */
    extractGapTextModels(rootElement, language, defaultLanguage) {
        const gapTexts = Array.from(rootElement.getElementsByTagName('gapText'));
        const gapTextModels = gapTexts.map((gapText) => {
            const gapTextModel = interactionParserUtils.getNonLanguageAttributes(gapText);
            gapTextModel.textContent = interactionParserUtils.getNodeContent(gapText, language, defaultLanguage);
            gapTextModel.matchMax = utilService.getIntOr(gapTextModel.matchMax);

            return gapTextModel;
        });

        gapTexts.forEach((gapTextElement) => {
            gapTextElement.parentNode.removeChild(gapTextElement);
        });

        return gapTextModels;
    },

    extractGapImgModels(interactionNode, language, defaultLanguage, assessmentRunId, questionIdentifier, mediaInfo) {
        const gapImages = Array.from(interactionNode.getElementsByTagName('gapImg'));
        const gapImgModels = Array.from(gapImages).map((gapImgElement) => {
            let mediaModel;
            const { identifier, matchMax, matchMin } = interactionParserUtils.getNonLanguageAttributes(gapImgElement);
            const objectElement = gapImgElement.getElementsByTagName('object')[0];
            const objectModel = interactionParserUtils.parseListOfChildren(
                gapImgElement,
                'object',
                language,
                defaultLanguage
            )[0];

            const dimensions = {
                width: utilService.getIntOr(objectModel.width),
                height: utilService.getIntOr(objectModel.height),
            };

            // When we are dealing with an Inspera authored interaction
            if (objectModel['inspera:logicalName'] !== undefined) {
                mediaModel = parseImage(questionIdentifier, objectElement, assessmentRunId, mediaInfo);
            }

            return { dimensions, identifier, matchMax, matchMin, mediaModel, textContent: objectModel.content, type: 'gapImg' };
        });

        gapImages.forEach((gapTextElement) => {
            gapTextElement.parentNode.removeChild(gapTextElement);
        });

        return gapImgModels;
    },

    /**
     * @param {Element} rootElement Will be modified!
     * @returns {*|Array}
     */
    extractAssociableHotspotModels(rootElement) {
        const doc = rootElement.ownerDocument;
        const gaps = Array.from(rootElement.getElementsByTagName('associableHotspot'));

        return gaps.map((gapElement) => {
            const placeholderId = `gap-placeholder-${uuid.v4()}`;
            const placeholder = doc.createElement('span');
            const coords = gapElement.getAttribute('coords');
            const [topLeftX, topLeftY, bottomRightX, bottomRightY] = coords.split(',');
            const height = bottomRightY - topLeftY;
            const width = bottomRightX - topLeftX;
            placeholder.textContent = ' ';
            placeholder.setAttribute('id', placeholderId);
            placeholder.setAttribute('class', 'gap-placeholder');
            gapElement.parentNode.replaceChild(placeholder, gapElement);

            return {
                coords: gapElement.getAttribute('coords'),
                identifier: gapElement.getAttribute('identifier'),
                matchMax: gapElement.getAttribute('matchMax'),
                position: { top: topLeftY, left: topLeftX },
                height,
                width,
                placeholderId,
            };
        });
    },

    extractPrompt(rootElement, language, defaultLanguage) {
        const prompt = interactionParserUtils.getPrompt(rootElement, language, defaultLanguage);
        Array.from(
            rootElement.getElementsByTagName('prompt')
        )
            .forEach((promptElement) => {
                promptElement.parentNode.removeChild(promptElement);
            });

        return prompt;
    },

    getBackgroundImageModel(interactionNode, assessmentRunId, questionIdentifier, legacyHeightAttribute, mediaInfo) {
        let imageModel = interactionParserUtils.getBackgroundImageModel(interactionNode, assessmentRunId, mediaInfo);
        if (!imageModel.logicalName) {
            imageModel = this.getDefaultImageModel();
        }

        const parsedLegacyHeight = parseInt(legacyHeightAttribute, 10);
        if ((!imageModel.height || isNaN(imageModel.height)) && !isNaN(parsedLegacyHeight)) {
            imageModel.height = parsedLegacyHeight;
        }
        if (!imageModel.width || isNaN(imageModel.width)) {
            imageModel.width = 620;
        }

        return imageModel;
    },

    getDefaultImageModel() {
        const imageModel = {};
        imageModel.isMainMedia = false;
        imageModel.logicalName = 'graphicgapmatch_default_bg';
        imageModel.mimeType = '';
        imageModel.orgWidth = 1240;
        imageModel.orgHeight = 600;
        imageModel.width = 800;
        imageModel.height = 387;
        imageModel.type = constants.mediaType.IMAGE;
        imageModel.objectType = 'content_image';
        imageModel.url = 'https://d2snzxottmona5.cloudfront.net/images/graphicgapmatch_default_bg.jpg';
        imageModel.id = 'graphicgapmatch_default_bg';
        return imageModel;
    },

    orderGapChoiceModels(gapChoiceModels, tokenOrder) {
        if (tokenOrder === 'random') {
            return utilService.shuffleArray(gapChoiceModels);
        }
        return gapChoiceModels;
    },

    parse(interactionNode, language, defaultLanguage, responseDeclarations, assessmentRunId, questionIdentifier, mediaInfo) {
        const canvasHeight = parseInt(interactionNode.getAttribute('inspera:canvasHeight'), 10);
        const interactionNodeClone = interactionNode.cloneNode(true);
        const gapTextModels = this.extractGapTextModels(interactionNodeClone, language, defaultLanguage);
        const gapImgModels = this.extractGapImgModels(
            interactionNode,
            language,
            defaultLanguage,
            assessmentRunId,
            questionIdentifier,
            mediaInfo,
        );
        const concatGapChoiceModels = gapImgModels.concat(gapTextModels);
        const hotspotModels = this.extractAssociableHotspotModels(interactionNodeClone);
        const prompt = this.extractPrompt(interactionNodeClone, language, defaultLanguage);
        const reuseAlternatives = interactionNodeClone.getAttribute('inspera:reuseAlternatives') === 'true' || false;
        const tokenPosition = interactionNodeClone.getAttribute('inspera:tokenPosition') || '';
        const tokenOrder = interactionNodeClone.getAttribute('inspera:tokenOrder') || '';
        const adjustGapChoicesToBeTheSameSize = interactionNodeClone.getAttribute('inspera:tokenSize') === 'autoSize';
        const gapChoiceModels = this.orderGapChoiceModels(concatGapChoiceModels, tokenOrder);

        const interactionBody = serializer.serializeToString(interactionNodeClone.childNodes).trim();

        const backgroundImageModel = this.getBackgroundImageModel(
            interactionNode,
            assessmentRunId,
            questionIdentifier,
            canvasHeight,
            mediaInfo
        );

        return {
            hotspotModels,
            backgroundImageModel,
            gapChoiceModels,
            interactionBody,
            prompt,
            reuseAlternatives,
            adjustGapChoicesToBeTheSameSize,
            tokenPosition,
            tokenOrder,
        };
    },
};

export default graphicGapMatchParser;

